import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Loader2, Lock, Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { analyzeHiringScan, unlockHiringScan } from "@/lib/api";
import { SCAN } from "@/constants/testIds";
import HiringScanReport from "@/components/HiringScanReport";
import GdprConsent from "@/components/GdprConsent";
import Seo, { serviceJsonLd, breadcrumbJsonLd } from "@/components/Seo";
import { trackLead } from "@/lib/pixel";
import { getUtmPayload } from "@/lib/utm";

const MOCKUP = "/images/hiring-scan-mockup.png";

const EMPTY_SCAN = { ruolo: "", seniority: "Senior", settore: "", area: "", dimensione_azienda: "" };
const EMPTY_GATE = { nome: "", cognome: "", azienda: "", ruolo_contatto: "", email: "", telefono: "" };

const RUOLI_CONTATTO = [
  "CEO / Founder",
  "Direttore HR",
  "Responsabile di funzione",
  "COO",
  "Operations Manager",
  "Altro",
];

export default function HiringScan() {
  const [scan, setScan] = useState(EMPTY_SCAN);
  const [gateOpen, setGateOpen] = useState(false);
  const [gate, setGate] = useState(EMPTY_GATE);
  const [gdpr, setGdpr] = useState(false);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [fee, setFee] = useState(null);

  const openGate = (e) => {
    e.preventDefault();
    if (!scan.ruolo || !scan.settore || !scan.area) {
      toast.error("Compila ruolo, settore e area geografica");
      return;
    }
    setGateOpen(true);
  };

  const submitGate = async (e) => {
    e.preventDefault();
    const { nome, cognome, azienda, ruolo_contatto, email, telefono } = gate;
    if (!nome || !cognome || !azienda || !ruolo_contatto || !email || !telefono) {
      toast.error("Tutti i campi sono obbligatori");
      return;
    }
    if (!gdpr) {
      toast.error("Devi accettare la Privacy Policy per procedere");
      return;
    }
    setLoading(true);
    try {
      const result = await analyzeHiringScan(scan);
      const unlocked = await unlockHiringScan({ scan_id: result.scan_id, ...gate, ...getUtmPayload() });
      trackLead({ content_name: "Hiring Scan", content_category: "hiring-scan" });
      setReport(result);
      setFee(unlocked);
      setGateOpen(false);
      toast.success("Report generato.");
      // scroll to top so the user lands on the hero of the report
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error("Errore nella generazione. Riprova.");
    } finally {
      setLoading(false);
    }
  };

  const resetAll = () => {
    setScan(EMPTY_SCAN);
    setGate(EMPTY_GATE);
    setReport(null);
    setFee(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  /* ------------------- REPORT VIEW ------------------- */
  if (report) {
    return (
      <HiringScanReport
        scan={scan}
        report={report}
        fee={fee}
        onRestart={resetAll}
      />
    );
  }

  /* ------------------- LANDING (opt-in) ------------------- */
  return (
    <>
      <Seo
        title="Hiring Scan gratuito, Quanto è difficile trovare la figura che cerchi? | Lovac"
        description="Genera in 60 secondi un report gratuito con difficulty score, profili disponibili, RAL media e time to hire per la figura che stai cercando. Dati di mercato italiano."
        path="/hiring-scan"
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            serviceJsonLd({
              name: "Hiring Scan, Report di mercato gratuito",
              description:
                "Report gratuito con difficulty score, profili disponibili, RAL media e time to hire per la figura cercata.",
              path: "/hiring-scan",
            }),
            breadcrumbJsonLd([
              { name: "Home", path: "/" },
              { name: "Hiring Scan", path: "/hiring-scan" },
            ]),
          ],
        }}
      />
      <section className="bg-lovac-dark text-on-dark grain relative overflow-hidden">
        <div className="container-lovac px-6 pt-20 pb-24 relative z-10 max-w-3xl mx-auto">
          <div className="text-center">
            <span className="badge-on-dark">Hiring Scan</span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-6 text-on-dark leading-[1.05]">
              Quanto è difficile<br />
              <span className="text-lovac-accent">trovare la figura che cerchi?</span>
            </h1>
            <p className="text-on-dark-muted text-lg mt-6 max-w-xl mx-auto leading-relaxed">
              Inserisci il ruolo, il settore e l'area geografica. Ottieni subito un report gratuito con difficulty score, profili disponibili nel mercato, RAL media e time to hire stimato.
            </p>
            <p className="text-sm text-on-dark mt-5">
              Nessun impegno. Report <strong className="font-semibold text-lovac-accent">generato in 60 secondi</strong>.
            </p>
          </div>

          <form onSubmit={openGate} id="scan-form" className="mt-12 card-dark space-y-4 scroll-mt-24" data-nocapture="true" data-ghl-ignore="true" data-lc-ignore="true">
            <p className="text-[11px] uppercase tracking-wider text-on-dark-muted mb-1">Dimmi cosa stai cercando</p>

            <div>
              <label className="text-xs text-on-dark-muted">Ruolo cercato</label>
              <input
                data-testid={SCAN.formRole}
                className="input-on-dark mt-1.5"
                placeholder="es. Architetto Software"
                value={scan.ruolo}
                onChange={(e) => setScan({ ...scan, ruolo: e.target.value })}
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-on-dark-muted">Seniority</label>
                <select
                  data-testid={SCAN.formSeniority}
                  className="input-on-dark mt-1.5"
                  value={scan.seniority}
                  onChange={(e) => setScan({ ...scan, seniority: e.target.value })}
                >
                  <option>Junior</option>
                  <option>Middle</option>
                  <option>Senior</option>
                  <option>C-level</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-on-dark-muted">Settore aziendale</label>
                <input
                  data-testid={SCAN.formSector}
                  className="input-on-dark mt-1.5"
                  placeholder="es. Software B2B"
                  value={scan.settore}
                  onChange={(e) => setScan({ ...scan, settore: e.target.value })}
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-on-dark-muted">Area geografica</label>
                <input
                  data-testid={SCAN.formArea}
                  className="input-on-dark mt-1.5"
                  placeholder="es. Marche / Milano"
                  value={scan.area}
                  onChange={(e) => setScan({ ...scan, area: e.target.value })}
                />
              </div>
              <div>
                <label className="text-xs text-on-dark-muted">Dimensione azienda <span className="opacity-60">(opz.)</span></label>
                <select
                  data-testid={SCAN.formSize}
                  className="input-on-dark mt-1.5"
                  value={scan.dimensione_azienda}
                  onChange={(e) => setScan({ ...scan, dimensione_azienda: e.target.value })}
                >
                  <option value="">—</option>
                  <option>1–10</option>
                  <option>11–50</option>
                  <option>51–250</option>
                  <option>250+</option>
                </select>
              </div>
            </div>

            <button type="submit" className="btn-primary w-full mt-3" data-testid={SCAN.submit}>
              Genera il report <ArrowRight size={16} />
            </button>

            <p className="text-[11px] text-on-dark-muted text-center pt-1">
              Report generato su dati pubblici e osservatori di settore.
            </p>
          </form>
        </div>
      </section>

      {/* Cosa trovi nel report + mockup */}
      <section className="py-24 bg-lovac">
        <div className="container-lovac grid lg:grid-cols-[1fr_1.1fr] gap-12 items-center">
          <div>
            <span className="badge-lovac">Cosa contiene</span>
            <h2 className="font-display text-4xl sm:text-5xl mt-5 text-lovac-dark leading-tight">
              Cosa trovi nel report.
            </h2>

            <ul className="mt-10 space-y-7 max-w-xl">
              {[
                ["Difficulty Score", "quanto è difficile trovare questa figura nel mercato italiano oggi."],
                ["Profili disponibili", "quante persone con quel profilo esistono nell'area che cerchi."],
                ["RAL media", "cosa si aspetta di guadagnare chi ha quel ruolo e quella seniority."],
                ["Time to hire stimato", "quanto ci vuole mediamente, e quanto ci vuole con Lovac."],
                ["Dove si trovano", "da quali settori vengono, quanto sono mobili, come si raggiungono."],
              ].map(([t, d]) => (
                <li key={t} className="flex items-start gap-4">
                  <div className="mt-2 h-1.5 w-1.5 rounded-full bg-lovac-accent shrink-0" />
                  <div>
                    <p className="text-lg font-display text-lovac-dark leading-tight">{t}</p>
                    <p className="text-base text-lovac-muted mt-1.5 leading-relaxed">{d}</p>
                  </div>
                </li>
              ))}
            </ul>

            <a
              href="#scan-form"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById("scan-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              className="btn-primary mt-10 inline-flex"
            >
              Compila il form e genera il report <ArrowRight size={16} />
            </a>
          </div>

          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-br from-[rgba(0,196,140,0.10)] via-transparent to-transparent rounded-3xl blur-2xl" />
            <img
              src={MOCKUP}
              alt="Esempio di report Hiring Scan Lovac su laptop"
              className="relative w-full h-auto"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* GATE MODAL */}
      <Dialog open={gateOpen} onOpenChange={(o) => !loading && setGateOpen(o)}>
        <DialogContent
          className="sm:max-w-md"
          style={{ background: "var(--lovac-dark)", borderColor: "var(--lovac-border-dark)", color: "var(--lovac-on-dark)" }}
        >
          <DialogHeader>
            <div className="flex items-center gap-2 mb-1">
              <Lock size={16} className="text-lovac-accent" />
              <p className="text-[11px] uppercase tracking-wider text-on-dark-muted">Ultimo passo</p>
            </div>
            <DialogTitle className="font-display text-2xl text-on-dark leading-tight">
              Dove vuoi ricevere il report?
            </DialogTitle>
            <DialogDescription className="text-on-dark-muted">
              Te lo mandiamo subito su <span className="text-lovac-accent">WhatsApp</span> e via email.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={submitGate} className="space-y-3 mt-2" data-nocapture="true" data-ghl-ignore="true" data-lc-ignore="true">
            <div className="grid grid-cols-2 gap-3">
              <input data-testid={SCAN.unlockNome} className="input-on-dark" placeholder="Nome" value={gate.nome} onChange={(e) => setGate({ ...gate, nome: e.target.value })} />
              <input data-testid={SCAN.unlockCognome} className="input-on-dark" placeholder="Cognome" value={gate.cognome} onChange={(e) => setGate({ ...gate, cognome: e.target.value })} />
            </div>
            <input data-testid={SCAN.unlockAzienda} className="input-on-dark" placeholder="Azienda" value={gate.azienda} onChange={(e) => setGate({ ...gate, azienda: e.target.value })} />
            <select
              data-testid="scan-unlock-ruolo"
              className="input-on-dark"
              value={gate.ruolo_contatto}
              onChange={(e) => setGate({ ...gate, ruolo_contatto: e.target.value })}
            >
              <option value="">Il tuo ruolo</option>
              {RUOLI_CONTATTO.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <input data-testid={SCAN.unlockEmail} type="email" className="input-on-dark" placeholder="Email aziendale" value={gate.email} onChange={(e) => setGate({ ...gate, email: e.target.value })} />
            <input data-testid={SCAN.unlockTel} className="input-on-dark" placeholder="Telefono / WhatsApp" value={gate.telefono} onChange={(e) => setGate({ ...gate, telefono: e.target.value })} />
            <div className="pt-2">
              <GdprConsent checked={gdpr} onChange={setGdpr} variant="dark" testId="scan-gdpr" />
            </div>
            <button type="submit" data-testid={SCAN.unlockSubmit} disabled={loading} className="btn-primary w-full mt-2">
              {loading ? (<><Loader2 size={16} className="animate-spin" /> Genero…</>) : (<><Sparkles size={16} /> Inviami il report</>)}
            </button>
            <p className="text-[11px] text-on-dark-muted text-center pt-1">
              I dati non verranno ceduti a terzi. Potresti essere contattato da un recruiter Lovac.
            </p>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
